// src/components/Header.jsx
import React, { useState } from "react";
import { Menu, X } from "lucide-react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { companyInfo, navigation } from "../data/mock";

const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Only allow these items in the top navigation
  const allowedNav = new Set([
    "Home",
    "About Us",
    "Services",
    "Payments",
    "Reviews",
    "Contact",
    "Apply",
  ]);
  const filteredNavigation = navigation.filter((item) => allowedNav.has(item.name));

  const doSmoothScroll = (hash) => {
    const element = document.querySelector(hash);
    if (!element) return;

    const offset = 20;
    const elementPosition = element.getBoundingClientRect().top + window.scrollY;

    window.scrollTo({
      top: elementPosition - offset,
      behavior: "smooth",
    });
  };

  const scrollToSection = (href) => {
    setIsMobileMenuOpen(false);

    // If we're not on the home page and need to scroll to a section, go home first
    if (location.pathname !== "/" && href.startsWith("#")) {
      navigate("/");
      setTimeout(() => doSmoothScroll(href), 150);
      return;
    }

    // Already on home: scroll immediately
    if (href.startsWith("#")) doSmoothScroll(href);
  };

  const handleNavClick = (e, item) => {
    // External links should behave like normal anchors
    if (item.external) return;

    // For pages/sections we control, prevent default and handle ourselves
    e.preventDefault();
    setIsMobileMenuOpen(false);

    if (item.isPage) {
      navigate(item.href);
      return;
    }

    scrollToSection(item.href);
  };

  return (
    <>
      <header className="header-nav">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Keep header height the same so nav text doesn't change */}
          <div className="flex items-center justify-between h-40">
            {/* Logo wrapper gives the image more room WITHOUT changing header height */}
            <Link
              to="/"
              onClick={() => setIsMobileMenuOpen(false)}
              className="flex items-center shrink-0"
              aria-label="Go to home"
            >
              <div className="relative w-[280px] sm:w-[340px] lg:w-[480px] xl:w-[540px] h-40">
                <img
                  src={companyInfo.logo}
                  alt="A Gentle Touch HomeCare - Compassionate Non Medical Home Care in Ohio"
                  className="absolute left-0 top-1/2 -translate-y-1/2 h-32 sm:h-36 lg:h-60 xl:h-64 w-auto object-contain"
                />
              </div>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-10">
              {filteredNavigation.map((item) =>
                item.external ? (
                  <a
                    key={item.name}
                    href={item.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-700 hover:text-[#008080] font-medium transition-colors duration-300 text-base"
                  >
                    {item.name}
                  </a>
                ) : item.isPage ? (
                  <Link
                    key={item.name}
                    to={item.href}
                    className="text-gray-700 hover:text-[#008080] font-medium transition-colors duration-300 text-base"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    {item.name}
                  </Link>
                ) : (
                  <a
                    key={item.name}
                    href={item.href}
                    onClick={(e) => handleNavClick(e, item)}
                    className="text-gray-700 hover:text-[#008080] font-medium transition-colors duration-300 text-base"
                  >
                    {item.name}
                  </a>
                )
              )}
            </nav>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden p-2 text-gray-700 hover:text-[#008080] transition-colors"
              aria-label="Toggle menu"
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="mobile-menu">
          <button
            onClick={() => setIsMobileMenuOpen(false)}
            className="absolute top-6 right-6 p-2 text-gray-700 hover:text-[#008080]"
            aria-label="Close menu"
          >
            <X className="w-8 h-8" />
          </button>

          <img
            src={companyInfo.logo}
            alt="A Gentle Touch HomeCare Logo"
            className="h-40 w-auto object-contain mb-8"
          />

          {filteredNavigation.map((item) =>
            item.external ? (
              <a
                key={item.name}
                href={item.href}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {item.name}
              </a>
            ) : item.isPage ? (
              <Link key={item.name} to={item.href} onClick={() => setIsMobileMenuOpen(false)}>
                {item.name}
              </Link>
            ) : (
              <a key={item.name} href={item.href} onClick={(e) => handleNavClick(e, item)}>
                {item.name}
              </a>
            )
          )}
        </div>
      )}
    </>
  );
};

export default Header;
